import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  Form,
  Input,
  List,
  Select,
  Typography,
  Collapse,
  Tooltip,
  Modal,
} from "antd";
import { SentinelExample } from "../../model_gen/sentinel";
import {
  DashedButton,
  OutlineButton,
  PrimaryButton,
} from "../shared/button/Buttons";
import CustomSwitch from "../shared/switch/CustomSwitch";
import { Sentinel } from "../../model_gen/sentinel";
import { Row, Col } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { SentinelService } from "../../services/SentinelService";
import { usePermissions } from "../../PermissionsProvider";
import { SeverityLevel } from "../../types/ISentinels";

interface SentinelFormProps {
  initialSentinel?: Sentinel;
  loading: boolean;
  setLoading?: (loading: boolean) => void;
  onSubmit: (formData: any) => void;
  onClose: () => void;
  submitButtonText: string;
  readOnly?: boolean;
  regenerationEnabled?: boolean;
  intentGroupId?: number;
}

interface SentinelGenerationExamples {
  id: number;
  exampleType: string;
  inputExample: string;
}

const SentinelForm: React.FC<SentinelFormProps> = ({
  initialSentinel,
  loading,
  setLoading,
  onSubmit,
  onClose,
  submitButtonText,
  readOnly = false,
  regenerationEnabled = false,
  intentGroupId,
}) => {
  const { apiKey } = usePermissions();
  const [sentinelName, setSentinelName] = useState(
    initialSentinel?.sentinel_name || "",
  );
  const [directive, setDirective] = useState(
    initialSentinel?.sentinel_directive?.directive_body?.main || "",
  );
  const [examples, setExamples] = useState<SentinelGenerationExamples[]>(
    initialSentinel?.sentinel_directive?.examples?.map(
      (example: SentinelExample, index: number) => ({
        id: index,
        exampleType: example.example_type,
        inputExample: example.example_body,
      }),
    ) || [],
  );
  const [severity, setSeverity] = useState(
    initialSentinel?.severity || SeverityLevel.INFO,
  );
  const [includeInput, setIncludeInput] = useState(
    initialSentinel?.meta?.include_input || "",
  );
  const [includeContext, setIncludeContext] = useState(
    initialSentinel?.meta?.input_include_context || "",
  );
  const [includeSchema, setIncludeSchema] = useState(
    initialSentinel?.meta?.input_include_schema || "",
  );
  const [includeSystemPrompt, setIncludeSystemPrompt] = useState(
    initialSentinel?.meta?.input_include_system || "",
  );
  const [includeToolCalls, setIncludeToolCalls] = useState(
    initialSentinel?.meta?.include_tool_calls || "",
  );
  const [qualificationType, setQualificationType] = useState(
    initialSentinel?.meta?.qualification_type || "",
  );
  const [qualificationDirective, setQualificationDirective] = useState(
    initialSentinel?.meta?.qualification_directive || "",
  );
  const [correctionType, setCorrectionType] = useState(
    initialSentinel?.meta?.correction_type || "",
  );
  const [correctionDirective, setCorrectionDirective] = useState(
    initialSentinel?.meta?.correction_directive || "",
  );

  const exampleIdCounter = useRef(
    initialSentinel?.sentinel_directive?.examples?.length || 0,
  );
  const [selectWidth, setSelectWidth] = useState<number>(0);

  const calculateWidestOption = useMemo(() => {
    const options = ["PASS", "FAULT", "NA"];
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (context) {
      context.font = "14px Arial";
      return (
        Math.max(
          ...options.map((option) => context.measureText(option).width),
        ) + 40
      );
    }
    return 80;
  }, []);

  useEffect(() => {
    setSelectWidth(calculateWidestOption);
  }, [calculateWidestOption]);

  useEffect(() => {
    // Update form state when initialSentinel changes
    setSentinelName(initialSentinel?.sentinel_name || "");
    setDirective(
      initialSentinel?.sentinel_directive?.directive_body?.main || "",
    );
    setExamples(
      initialSentinel?.sentinel_directive?.examples?.map(
        (example: SentinelExample, index: number) => ({
          id: index,
          exampleType: example.example_type,
          inputExample: example.example_body,
        }),
      ) || [],
    );
    setSeverity(initialSentinel?.severity || SeverityLevel.INFO);
    setIncludeInput(initialSentinel?.meta?.include_input || "");
    setIncludeContext(initialSentinel?.meta?.input_include_context || "");
    setIncludeSchema(initialSentinel?.meta?.input_include_schema || "");
    setIncludeSystemPrompt(initialSentinel?.meta?.input_include_system || "");
    setIncludeToolCalls(initialSentinel?.meta?.include_tool_calls || "");
    setQualificationType(initialSentinel?.meta?.qualification_type || "");
    setQualificationDirective(
      initialSentinel?.meta?.qualification_directive || "",
    );
    setCorrectionType(initialSentinel?.meta?.correction_type || "");
    setCorrectionDirective(initialSentinel?.meta?.correction_directive || "");
  }, [initialSentinel]);

  const handleAddExample = useCallback(() => {
    setExamples((prev) => [
      ...prev,
      { id: exampleIdCounter.current++, exampleType: "", inputExample: "" },
    ]);
  }, []);

  const handleRemoveExample = useCallback((itemId: number) => {
    setExamples((prev) => prev.filter((entry) => entry.id !== itemId));
  }, []);

  const handleExampleTypeChange = useCallback(
    (itemId: number, newType: string) => {
      setExamples((prev) =>
        prev.map((entry) =>
          entry.id === itemId ? { ...entry, exampleType: newType } : entry,
        ),
      );
    },
    [],
  );

  const handleInputExampleChange = useCallback(
    (itemId: number, newInput: string) => {
      setExamples((prev) =>
        prev.map((entry) =>
          entry.id === itemId ? { ...entry, inputExample: newInput } : entry,
        ),
      );
    },
    [],
  );

  const handleSubmit = () => {
    const updatedSentinel: Sentinel = {
      id: initialSentinel?.id || 0,
      sentinel_name: sentinelName,
      date_created: initialSentinel?.date_created || 0,
      sentinel_directive: {
        id: initialSentinel?.sentinel_directive?.id || 0,
        sentinel_id: initialSentinel?.sentinel_directive?.sentinel_id || 0,
        date_created: initialSentinel?.sentinel_directive?.date_created || 0,
        directive_body: {
          main: directive,
          override:
            initialSentinel?.sentinel_directive?.directive_body?.override || "",
        },
        examples: examples.map((example) => ({
          example_type: example.exampleType,
          example_body: example.inputExample,
        })),
      },
      state: initialSentinel?.state || "",
      severity: severity,
      meta: {
        ...initialSentinel?.meta,
        include_input: includeInput,
        input_include_context: includeContext,
        input_include_system: includeSystemPrompt,
        input_include_schema: includeSchema,
        include_tool_calls: includeToolCalls,
        qualification_type: qualificationType,
        qualification_directive: qualificationDirective,
        correction_type: correctionType,
        correction_directive: correctionDirective,
      },
      sentinel_generation_id: initialSentinel?.sentinel_generation_id || 0,
      qa: initialSentinel?.qa || [],
      intent_group_id: intentGroupId || initialSentinel?.intent_group_id,
    };

    onSubmit(updatedSentinel);
  };

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [activeRegenerateFunction, setActiveRegenerateFunction] = useState<
    ((feedback: string) => void) | null
  >(null);
  const [activeSection, setActiveSection] = useState<
    "Qualification" | "Evaluation" | "Correction" | "Sentinel" | null
  >(null);
  const [expectationText, setExpectationText] = useState("");
  const [conditionText, setConditionText] = useState("");

  const handleQualificationRegenerate = useCallback(() => {
    setActiveSection("Qualification");
    setActiveRegenerateFunction(() => (feedback: string) => {
      if (!initialSentinel?.id) return;

      const payload: any = { feedback };
      if (!initialSentinel?.meta?.expectation && expectationText) {
        payload.expectation = expectationText;
      }
      if (!initialSentinel?.meta?.condition && conditionText) {
        payload.condition = conditionText;
      }

      setLoading?.(true);
      SentinelService.regenerateSentinelQualification(
        initialSentinel.id,
        payload,
        null,
        apiKey,
      ).subscribe(
        (regeneratedSentinel: Sentinel) => {
          if (!initialSentinel?.meta?.expectation) {
            initialSentinel.meta.expectation = expectationText;
          }
          if (!initialSentinel?.meta?.condition) {
            initialSentinel.meta.condition = conditionText;
          }
          setQualificationType(
            regeneratedSentinel.meta?.qualification_type || "",
          );
          setQualificationDirective(
            regeneratedSentinel.meta?.qualification_directive || "",
          );
          setLoading?.(false);
        },
        (error: any) => {
          console.error("Error regenerating qualification:", error);
          setLoading?.(false);
        },
      );
    });
    setIsFeedbackModalOpen(true);
  }, [initialSentinel?.id, setLoading, apiKey, expectationText, conditionText]);

  const handleEvaluationRegenerate = useCallback(() => {
    setActiveSection("Evaluation");
    setActiveRegenerateFunction(() => (feedback: string) => {
      if (!initialSentinel?.id) return;

      const payload: any = { feedback };
      if (!initialSentinel?.meta?.expectation && expectationText) {
        payload.expectation = expectationText;
      }
      if (!initialSentinel?.meta?.condition && conditionText) {
        payload.condition = conditionText;
      }

      setLoading?.(true);
      SentinelService.regenerateSentinelEvaluation(
        initialSentinel.id,
        payload,
        null,
        apiKey,
      ).subscribe(
        (regeneratedSentinel: Sentinel) => {
          if (!initialSentinel?.meta?.expectation) {
            initialSentinel.meta.expectation = expectationText;
          }
          if (!initialSentinel?.meta?.condition) {
            initialSentinel.meta.condition = conditionText;
          }
          setDirective(
            regeneratedSentinel.sentinel_directive?.directive_body?.main || "",
          );
          setExamples(
            regeneratedSentinel.sentinel_directive?.examples?.map(
              (example: SentinelExample, index: number) => ({
                id: index,
                exampleType: example.example_type,
                inputExample: example.example_body,
              }),
            ) || [],
          );
          setLoading?.(false);
        },
        (error: any) => {
          console.error("Error regenerating evaluation:", error);
          setLoading?.(false);
        },
      );
    });
    setIsFeedbackModalOpen(true);
  }, [initialSentinel?.id, setLoading, apiKey]);

  const handleCorrectionRegenerate = useCallback(() => {
    setActiveSection("Correction");
    setActiveRegenerateFunction(() => (feedback: string) => {
      if (!initialSentinel?.id) return;

      const payload: any = { feedback };
      if (!initialSentinel?.meta?.expectation && expectationText) {
        payload.expectation = expectationText;
      }
      if (!initialSentinel?.meta?.condition && conditionText) {
        payload.condition = conditionText;
      }

      setLoading?.(true);
      SentinelService.regenerateSentinelCorrection(
        initialSentinel.id,
        payload,
        null,
        apiKey,
      ).subscribe(
        (regeneratedSentinel: Sentinel) => {
          if (!initialSentinel?.meta?.expectation) {
            initialSentinel.meta.expectation = expectationText;
          }
          if (!initialSentinel?.meta?.condition) {
            initialSentinel.meta.condition = conditionText;
          }
          setCorrectionType(regeneratedSentinel.meta?.correction_type || "");
          setCorrectionDirective(
            regeneratedSentinel.meta?.correction_directive || "",
          );
          setLoading?.(false);
        },
        (error: any) => {
          console.error("Error regenerating correction:", error);
          setLoading?.(false);
        },
      );
    });
    setIsFeedbackModalOpen(true);
  }, [initialSentinel?.id, setLoading, apiKey]);

  const handleSentinelRegenerate = useCallback(() => {
    setActiveSection("Sentinel");
    setActiveRegenerateFunction(() => (feedback: string) => {
      if (!initialSentinel?.id) return;

      const payload: any = { feedback };
      if (!initialSentinel?.meta?.expectation && expectationText) {
        payload.expectation = expectationText;
      }
      if (!initialSentinel?.meta?.condition && conditionText) {
        payload.condition = conditionText;
      }

      setLoading?.(true);
      SentinelService.regenerateSentinel(
        initialSentinel.id,
        payload,
        null,
        apiKey,
      ).subscribe(
        (regeneratedSentinel: Sentinel) => {
          if (!initialSentinel?.meta?.expectation) {
            initialSentinel.meta.expectation = expectationText;
          }
          if (!initialSentinel?.meta?.condition) {
            initialSentinel.meta.condition = conditionText;
          }
          setSentinelName(regeneratedSentinel.sentinel_name || "");
          setDirective(
            regeneratedSentinel.sentinel_directive?.directive_body?.main || "",
          );
          setExamples(
            regeneratedSentinel.sentinel_directive?.examples?.map(
              (example: SentinelExample, index: number) => ({
                id: index,
                exampleType: example.example_type,
                inputExample: example.example_body,
              }),
            ) || [],
          );
          setSeverity(regeneratedSentinel.severity || SeverityLevel.INFO);
          setQualificationType(
            regeneratedSentinel.meta?.qualification_type || "",
          );
          setQualificationDirective(
            regeneratedSentinel.meta?.qualification_directive || "",
          );
          setCorrectionType(regeneratedSentinel.meta?.correction_type || "");
          setCorrectionDirective(
            regeneratedSentinel.meta?.correction_directive || "",
          );
          setLoading?.(false);
        },
        (error: any) => {
          console.error("Error regenerating sentinel:", error);
          setLoading?.(false);
        },
      );
    });
    setIsFeedbackModalOpen(true);
  }, [initialSentinel?.id, setLoading, apiKey]);

  const handleModalSubmit = () => {
    if (activeRegenerateFunction) {
      activeRegenerateFunction(feedbackText);
    }
    setFeedbackText("");
    setExpectationText("");
    setConditionText("");
    setIsFeedbackModalOpen(false);
    setActiveRegenerateFunction(null);
    setActiveSection(null);
  };

  const handleModalCancel = () => {
    setFeedbackText("");
    setExpectationText("");
    setConditionText("");
    setIsFeedbackModalOpen(false);
    setActiveRegenerateFunction(null);
    setActiveSection(null);
  };

  return (
    <Form layout="vertical" style={{ width: "100%" }}>
      <Form.Item label="Name" required tooltip="Your name for this Sentinel">
        <Input
          value={sentinelName}
          onChange={(e) => setSentinelName(e.target.value)}
          placeholder="Menu Monitor"
          disabled={loading || readOnly}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Severity"
            required
            tooltip="Select the severity level for this Sentinel">
            <Select
              value={severity}
              onChange={(value) => setSeverity(value)}
              disabled={loading || readOnly}>
              {Object.entries(SeverityLevel)
                .filter(
                  ([key]) =>
                    !isNaN(
                      Number(SeverityLevel[key as keyof typeof SeverityLevel]),
                    ),
                )
                .map(([key]) => (
                  <Select.Option
                    key={SeverityLevel[key as keyof typeof SeverityLevel]}
                    value={SeverityLevel[key as keyof typeof SeverityLevel]}>
                    {key}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Collapse defaultActiveKey={["evaluation"]} style={{ marginBottom: 24 }}>
        {qualificationType || qualificationDirective ? (
          <Collapse.Panel
            header={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}>
                <Typography.Text strong>Qualification</Typography.Text>
                {regenerationEnabled && (
                  <Tooltip title="Regenerate Qualification">
                    <SyncOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQualificationRegenerate();
                      }}
                      style={{ cursor: "pointer", color: "#21b892" }}
                      spin={loading}
                    />
                  </Tooltip>
                )}
              </div>
            }
            key="qualification">
            <Form.Item
              label="Type"
              required
              tooltip="Select the type of qualification">
              <Select
                value={qualificationType}
                onChange={(value) => setQualificationType(value)}
                disabled={loading || readOnly}>
                <Select.Option value="STATIC">STATIC</Select.Option>
                <Select.Option value="LLM">LLM</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Directive"
              required
              tooltip="Tells the sentinel how to qualify the input">
              <Input.TextArea
                style={{ whiteSpace: "pre-wrap" }}
                value={qualificationDirective as string}
                onChange={(e) => setQualificationDirective(e.target.value)}
                rows={4}
                placeholder="Specify how to qualify the input"
                disabled={loading || readOnly}
              />
            </Form.Item>
          </Collapse.Panel>
        ) : (
          regenerationEnabled && (
            <div
              style={{
                padding: "12px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #d9d9d9",
              }}>
              <Typography.Text strong>Qualification</Typography.Text>
              <PrimaryButton
                onClick={handleQualificationRegenerate}
                disabled={loading}
                size="small">
                Generate
              </PrimaryButton>
            </div>
          )
        )}

        <Collapse.Panel
          header={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}>
              <Typography.Text strong>Evaluation</Typography.Text>
              {regenerationEnabled && (
                <Tooltip title="Regenerate Evaluation">
                  <SyncOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEvaluationRegenerate();
                    }}
                    style={{ cursor: "pointer", color: "#21b892" }}
                    spin={loading}
                  />
                </Tooltip>
              )}
            </div>
          }
          key="evaluation">
          <Form.Item
            label="Directive"
            required
            tooltip="Tells the sentinel what to monitor for">
            <Input.TextArea
              style={{ whiteSpace: "pre-wrap" }}
              value={directive}
              onChange={(e) => setDirective(e.target.value)}
              rows={4}
              placeholder="Make sure the customer doesn't order items that aren't on the menu"
              disabled={loading || readOnly}
            />
          </Form.Item>

          <Form.Item>
            <Collapse
              defaultActiveKey={[]}
              style={{
                backgroundColor: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
              }}>
              <Collapse.Panel
                header={<Typography.Text strong>Examples</Typography.Text>}
                key="examples"
                style={{ padding: "4px 0" }}>
                <div style={{ padding: "8px 0" }}>
                  <List
                    dataSource={examples}
                    renderItem={(item: SentinelGenerationExamples) => (
                      <List.Item key={item.id} style={{ padding: "8px 0" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            width: "100%",
                          }}>
                          <div
                            style={{
                              width: `${selectWidth}px`,
                              marginRight: "16px",
                            }}>
                            <Select
                              style={{ width: "100%" }}
                              value={item.exampleType}
                              onChange={(value) =>
                                handleExampleTypeChange(item.id, value)
                              }
                              placeholder="Select type"
                              disabled={loading || readOnly}>
                              <Select.Option
                                value="PASS"
                                style={{ color: "green" }}>
                                <span style={{ color: "green" }}>PASS</span>
                              </Select.Option>
                              <Select.Option
                                value="FAULT"
                                style={{ color: "red" }}>
                                <span style={{ color: "red" }}>FAULT</span>
                              </Select.Option>
                              <Select.Option
                                value="NA"
                                style={{ color: "grey" }}>
                                <span style={{ color: "grey" }}>NA</span>
                              </Select.Option>
                            </Select>
                          </div>
                          <Input.TextArea
                            style={{
                              flex: 1,
                              marginRight: "16px",
                              whiteSpace: "pre-wrap",
                            }}
                            value={item.inputExample}
                            onChange={(e) =>
                              handleInputExampleChange(item.id, e.target.value)
                            }
                            placeholder="Example"
                            rows={4}
                            disabled={loading || readOnly}
                          />
                          <OutlineButton
                            onClick={() => handleRemoveExample(item.id)}
                            disabled={loading || readOnly}>
                            -
                          </OutlineButton>
                        </div>
                      </List.Item>
                    )}
                  />
                  <List.Item style={{ padding: "8px 0 0 0" }}>
                    <DashedButton
                      onClick={handleAddExample}
                      block
                      disabled={loading || readOnly}>
                      + Add Example
                    </DashedButton>
                  </List.Item>
                </div>
              </Collapse.Panel>
            </Collapse>
          </Form.Item>
        </Collapse.Panel>

        {correctionType || correctionDirective ? (
          <Collapse.Panel
            header={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}>
                <Typography.Text strong>Correction</Typography.Text>
                {regenerationEnabled && (
                  <Tooltip title="Regenerate Correction">
                    <SyncOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCorrectionRegenerate();
                      }}
                      style={{ cursor: "pointer", color: "#21b892" }}
                      spin={loading}
                    />
                  </Tooltip>
                )}
              </div>
            }
            key="correction">
            <Form.Item
              label="Type"
              required
              tooltip="Select the type of correction">
              <Select
                value={correctionType}
                onChange={(value) => setCorrectionType(value)}
                disabled={loading || readOnly}>
                <Select.Option value="STATIC">STATIC</Select.Option>
                <Select.Option value="LLM">LLM</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Directive"
              required
              tooltip="Tells the sentinel how to correct the input">
              <Input.TextArea
                style={{ whiteSpace: "pre-wrap" }}
                value={correctionDirective as string}
                onChange={(e) => setCorrectionDirective(e.target.value)}
                rows={4}
                placeholder="Specify how to correct the input"
                disabled={loading || readOnly}
              />
            </Form.Item>
          </Collapse.Panel>
        ) : (
          regenerationEnabled && (
            <div
              style={{
                padding: "12px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #d9d9d9",
              }}>
              <Typography.Text strong>Correction</Typography.Text>
              <PrimaryButton
                onClick={handleCorrectionRegenerate}
                disabled={loading}
                size="small">
                Generate
              </PrimaryButton>
            </div>
          )
        )}
      </Collapse>

      <Form.Item label="Include Options">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomSwitch
                checked={includeSystemPrompt === "true"}
                onChange={(checked) =>
                  setIncludeSystemPrompt(checked ? "true" : "")
                }
                disabled={loading || readOnly}
              />
              <span style={{ marginLeft: "8px" }}>System Prompt</span>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomSwitch
                checked={includeContext === "true"}
                onChange={(checked) => setIncludeContext(checked ? "true" : "")}
                disabled={loading || readOnly}
              />
              <span style={{ marginLeft: "8px" }}>Context</span>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomSwitch
                checked={includeInput === "true"}
                onChange={(checked) => setIncludeInput(checked ? "true" : "")}
                disabled={loading || readOnly}
              />
              <span style={{ marginLeft: "8px" }}>Input</span>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomSwitch
                checked={includeToolCalls === "true"}
                onChange={(checked) =>
                  setIncludeToolCalls(checked ? "true" : "")
                }
                disabled={loading || readOnly}
              />
              <span style={{ marginLeft: "8px" }}>Tool Calls</span>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomSwitch
                checked={includeSchema === "true"}
                onChange={(checked) => setIncludeSchema(checked ? "true" : "")}
                disabled={loading || readOnly}
              />
              <span style={{ marginLeft: "8px" }}>JSON Schema</span>
            </div>
          </Col>
        </Row>
      </Form.Item>
      {!readOnly && (
        <Form.Item>
          <PrimaryButton
            onClick={handleSubmit}
            style={{ marginRight: "10px" }}
            loading={loading}
            disabled={loading}>
            {submitButtonText}
          </PrimaryButton>
          {regenerationEnabled && (
            <PrimaryButton
              onClick={handleSentinelRegenerate}
              style={{ marginRight: "10px" }}
              loading={loading}
              disabled={loading}
              icon={<SyncOutlined />}>
              Regenerate
            </PrimaryButton>
          )}
          <OutlineButton onClick={onClose} disabled={loading}>
            Cancel
          </OutlineButton>
        </Form.Item>
      )}

      <Modal
        title={activeSection ? `Regenerate ${activeSection}` : ""}
        open={isFeedbackModalOpen}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        okText="Submit"
        cancelText="Cancel">
        <Form layout="vertical">
          <Form.Item
            label="Feedback"
            tooltip="Provides user feedback to be considered during the regeneration operations">
            <Input.TextArea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Enter your feedback here..."
              rows={4}
            />
          </Form.Item>

          {!initialSentinel?.meta?.expectation && (
            <Form.Item
              label="Expectation"
              tooltip="Define the expected behavior">
              <Input.TextArea
                value={expectationText}
                onChange={(e) => setExpectationText(e.target.value)}
                placeholder="Enter the expected behavior..."
                rows={4}
              />
            </Form.Item>
          )}

          {!initialSentinel?.meta?.condition && (
            <Form.Item
              label="Condition"
              tooltip="Define the condition for this sentinel">
              <Input.TextArea
                value={conditionText}
                onChange={(e) => setConditionText(e.target.value)}
                placeholder="Enter the condition..."
                rows={4}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Form>
  );
};

export default SentinelForm;
